import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScanFaceIcon, CheckCircle2Icon, ShieldCheckIcon } from 'lucide-react';
interface WelcomeScreenProps {
  ceoName: string;
  onComplete: () => void;
}
export function WelcomeScreen({
  ceoName,
  onComplete
}: WelcomeScreenProps) {
  const [stage, setStage] = useState<'scanning' | 'recognized' | 'welcome'>('scanning');
  useEffect(() => {
    const timer1 = setTimeout(() => setStage('recognized'), 2000);
    const timer2 = setTimeout(() => setStage('welcome'), 3500);
    const timer3 = setTimeout(() => onComplete(), 5500);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [onComplete]);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 bg-[#0D0F14] flex items-center justify-center z-50" data-id="element-652">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-texture" data-id="element-653" />

      {/* Animated rings */}
      <div className="absolute inset-0 flex items-center justify-center" data-id="element-654">
        {[...Array(3)].map((_, i) => <motion.div key={i} initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: [0.8, 1.5, 2],
        opacity: [0.3, 0.1, 0]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        delay: i * 0.8,
        ease: 'easeOut'
      }} className="absolute w-64 h-64 rounded-full border border-[#C9A84C]" data-id="element-655" />)}
      </div>

      <div className="relative z-10 text-center" data-id="element-656">
        <AnimatePresence mode="wait" data-id="element-657">
          {stage === 'scanning' && <motion.div key="scanning" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.9
        }} className="flex flex-col items-center" data-id="element-658">
              <motion.div animate={{
            boxShadow: ['0 0 0 0 rgba(201, 168, 76, 0.4)', '0 0 0 30px rgba(201, 168, 76, 0)']
          }} transition={{
            duration: 1.5,
            repeat: Infinity
          }} className="w-32 h-32 rounded-full bg-[rgba(201,168,76,0.1)] border-2 border-[#C9A84C] flex items-center justify-center mb-8" data-id="element-659">
                <ScanFaceIcon className="w-16 h-16 text-[#C9A84C]" data-id="element-660" />
              </motion.div>
              <h2 className="text-3xl font-['Playfair_Display'] text-white mb-3" data-id="element-661">
                Identifying...
              </h2>
              <p className="text-gray-400" data-id="element-662">Please look at the camera</p>

              {/* Scanning line animation */}
              <motion.div animate={{
            y: [-40, 40]
          }} transition={{
            duration: 1.5,
            repeat: Infinity,
            repeatType: 'reverse'
          }} className="w-48 h-0.5 bg-gradient-to-r from-transparent via-[#C9A84C] to-transparent mt-8" data-id="element-663" />
            </motion.div>}

          {stage === 'recognized' && <motion.div key="recognized" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.9
        }} className="flex flex-col items-center" data-id="element-664">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200
          }} className="w-32 h-32 rounded-full bg-[rgba(34,197,94,0.1)] border-2 border-green-500 flex items-center justify-center mb-8" data-id="element-665">
                <CheckCircle2Icon className="w-16 h-16 text-green-500" data-id="element-666" />
              </motion.div>
              <h2 className="text-3xl font-['Playfair_Display'] text-white mb-3" data-id="element-667">
                Identity Verified
              </h2>
              <div className="flex items-center gap-2 text-green-400" data-id="element-668">
                <ShieldCheckIcon className="w-5 h-5" data-id="element-669" />
                <span data-id="element-670">Biometric authentication successful</span>
              </div>
            </motion.div>}

          {stage === 'welcome' && <motion.div key="welcome" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} className="flex flex-col items-center" data-id="element-671">
              <motion.div initial={{
            scale: 0.8
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150
          }} className="mb-8" data-id="element-672">
                <div className="w-40 h-40 rounded-full bg-gradient-to-br from-[#C9A84C] to-[#D4AF37] flex items-center justify-center text-6xl font-['Playfair_Display'] text-[#0D0F14] font-bold shadow-2xl shadow-[rgba(201,168,76,0.3)]" data-id="element-673">
                  {ceoName.split(' ').map(n => n[0]).join('')}
                </div>
              </motion.div>
              <h1 className="text-5xl font-['Playfair_Display'] mb-4" data-id="element-674">
                <span className="text-white" data-id="element-675">Good Morning, </span>
                <span className="text-gold-gradient" data-id="element-676">{ceoName}</span>
              </h1>
              <p className="text-xl text-gray-400" data-id="element-677">
                Your executive workspace is ready
              </p>

              {/* Loading bar */}
              <div className="w-64 h-1 bg-[rgba(255,255,255,0.1)] rounded-full mt-8 overflow-hidden" data-id="element-678">
                <motion.div initial={{
              width: 0
            }} animate={{
              width: '100%'
            }} transition={{
              duration: 2,
              ease: 'easeInOut'
            }} className="h-full bg-gradient-to-r from-[#C9A84C] to-[#D4AF37]" data-id="element-679" />
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Qubix branding */}
      <div className="absolute bottom-8 right-8 text-[#C9A84C] opacity-50 text-sm tracking-widest" data-id="element-680">
        Powered by QUBIX
      </div>
    </motion.div>;
}